import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PiktableLoader } from "@/components/PiktableLoader";
import { SendLeadEmailDialog } from "@/components/admin/SendLeadEmailDialog";
import { LeadEmailHistoryDialog } from "@/components/admin/LeadEmailHistoryDialog";
import { LeadCallLogDialog } from "@/components/admin/LeadCallLogDialog";
import { type LeadEmailType } from "@/hooks/useSendLeadEmail";
import { useLeadCallLogCounts } from "@/hooks/useLeadCallLogs";
import {
  useAdminLeads,
  getStatusLabel,
  getSourceLabel,
  getSourceIcon,
  getStatusColor,
  type LeadStatus,
  type LeadSource,
  type Lead,
} from "@/hooks/useAdminLeads";
import {
  Target,
  ArrowLeft,
  Search,
  MoreHorizontal,
  MessageSquare,
  Phone,
  Mail,
  Building2,
  MapPin,
  Calendar,
  TrendingUp,
  Users,
  ChevronDown,
  RefreshCw,
  Download,
  Plus,
  Send,
  Trash2,
  Pencil,
  FileText,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { format, formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

export default function AdminLeads() {
  const {
    leads,
    stats,
    loading,
    filters,
    setFilters,
    updateLeadStatus,
    updateLeadNotes,
    addManualLead,
    deleteLead,
    updateLead,
    refetch,
  } = useAdminLeads();

  const { counts: callLogCounts, fetchCounts: fetchCallLogCounts } = useLeadCallLogCounts();

  // URL-based dialog state
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Read dialog state from URL
  const dialogType = searchParams.get('dialog');
  const dialogLeadId = searchParams.get('leadId');
  const dialogEmailType = searchParams.get('emailType') as LeadEmailType | null;

  // Find the lead corresponding to the URL state
  const dialogLead = dialogLeadId ? leads.find(l => l.id === dialogLeadId) || null : null;

  // Derived dialog open states
  const callLogDialogOpen = dialogType === 'callLog' && !!dialogLead;
  const emailDialogOpen = dialogType === 'email' && !!dialogLead;
  const emailHistoryOpen = dialogType === 'emailHistory' && !!dialogLead;
  const noteDialogOpen = dialogType === 'note' && !!dialogLead;
  const editDialogOpen = dialogType === 'edit' && !!dialogLead;
  const deleteDialogOpen = dialogType === 'delete' && !!dialogLead;
  const addDialogOpen = dialogType === 'add';

  // Local form states (not persisted in URL)
  const [noteText, setNoteText] = useState("");
  const [editingLead, setEditingLead] = useState<Partial<Lead> | null>(null);
  const [newLead, setNewLead] = useState({
    full_name: "",
    email: "",
    phone: "",
    establishment_name: "",
    city: "",
    notes: "",
  });

  // Helper functions to manage URL dialog state
  const openDialog = useCallback((type: string, leadId?: string, extra?: Record<string, string>) => {
    const params = new URLSearchParams(searchParams);
    params.set('dialog', type);
    if (leadId) params.set('leadId', leadId);
    if (extra) {
      Object.entries(extra).forEach(([key, value]) => params.set(key, value));
    }
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const closeDialog = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('dialog');
    params.delete('leadId');
    params.delete('emailType');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Sync local form state when dialog opens
  useEffect(() => {
    if (dialogType === 'note' && dialogLead) {
      setNoteText(dialogLead.notes || "");
    }
  }, [dialogType, dialogLead]);

  useEffect(() => {
    if (dialogType === 'edit' && dialogLead) {
      setEditingLead({
        id: dialogLead.id,
        full_name: dialogLead.full_name,
        email: dialogLead.email,
        phone: dialogLead.phone || "",
        establishment_name: dialogLead.establishment_name || "",
        establishment_type: dialogLead.establishment_type || "",
        seating_capacity: dialogLead.seating_capacity,
        city: dialogLead.city || "",
        interested_plan: dialogLead.interested_plan || "",
        contact_preference: dialogLead.contact_preference || "",
        notes: dialogLead.notes || "",
        current_booking_channels: dialogLead.current_booking_channels || [],
        current_management_system: dialogLead.current_management_system || [],
      });
    }
  }, [dialogType, dialogLead]);

  useEffect(() => {
    if (dialogType === 'add') {
      setNewLead({
        full_name: "",
        email: "",
        phone: "",
        establishment_name: "",
        city: "",
        notes: "",
      });
    }
  }, [dialogType]);

  // Fetch call log counts when leads change
  useEffect(() => {
    if (leads.length > 0) {
      fetchCallLogCounts(leads.map(l => l.id));
    }
  }, [leads, fetchCallLogCounts]);

  const handleStatusChange = async (lead: Lead, newStatus: LeadStatus) => {
    await updateLeadStatus(lead.id, newStatus);
  };

  const handleOpenNoteDialog = (lead: Lead) => {
    openDialog('note', lead.id);
  };

  const handleSaveNotes = async () => {
    if (dialogLead) {
      await updateLeadNotes(dialogLead.id, noteText);
      closeDialog();
    }
  };

  const handleAddLead = async () => {
    if (newLead.full_name && newLead.email) {
      await addManualLead(newLead);
      closeDialog();
      setNewLead({
        full_name: "",
        email: "",
        phone: "",
        establishment_name: "",
        city: "",
        notes: "",
      });
    }
  };

  const handleOpenEmailDialog = (lead: Lead, type: LeadEmailType) => {
    openDialog('email', lead.id, { emailType: type });
  };

  const handleDeleteLead = (lead: Lead) => {
    openDialog('delete', lead.id);
  };

  const confirmDeleteLead = async () => {
    if (dialogLead) {
      await deleteLead(dialogLead.id);
      closeDialog();
    }
  };

  const handleOpenEditDialog = (lead: Lead) => {
    openDialog('edit', lead.id);
  };

  const handleSaveEdit = async () => {
    if (editingLead?.id) {
      await updateLead(editingLead.id, editingLead);
      closeDialog();
      setEditingLead(null);
    }
  };

  const handleExportCSV = () => {
    const headers = ["Nom", "Email", "Téléphone", "Établissement", "Ville", "Source", "Statut", "Date"];
    const rows = leads.map(lead => [
      lead.full_name,
      lead.email,
      lead.phone || "",
      lead.establishment_name || "",
      lead.city || "",
      getSourceLabel(lead.source),
      getStatusLabel(lead.status),
      format(new Date(lead.created_at), "dd/MM/yyyy HH:mm"),
    ]);

    const csv = [headers.join(","), ...rows.map(row => row.map(cell => `"${cell}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `prospects_${format(new Date(), "yyyy-MM-dd")}.csv`);
    link.click();
  };

  if (loading && leads.length === 0) {
    return <PiktableLoader message="Chargement des prospects..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="container mx-auto p-6 max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-serif font-bold flex items-center gap-3">
                <Target className="w-7 h-7 text-pink-500" />
                Gestion des prospects
              </h1>
              <p className="text-muted-foreground">
                Suivi des leads entrants (web, Facebook, etc.)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => refetch()} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </Button>
            <Button variant="outline" onClick={handleExportCSV}>
              <Download className="w-4 h-4 mr-2" />
              Exporter CSV
            </Button>
            <Dialog open={addDialogOpen} onOpenChange={(open) => open ? openDialog('add') : closeDialog()}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ajouter un prospect manuellement</DialogTitle>
                  <DialogDescription>
                    Ajoutez un prospect depuis une autre source (téléphone, email direct, etc.)
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <Input
                    placeholder="Nom complet *"
                    value={newLead.full_name}
                    onChange={(e) => setNewLead({ ...newLead, full_name: e.target.value })}
                  />
                  <Input
                    placeholder="Email *"
                    type="email"
                    value={newLead.email}
                    onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                  />
                  <Input
                    placeholder="Téléphone"
                    value={newLead.phone}
                    onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                  />
                  <Input
                    placeholder="Nom de l'établissement"
                    value={newLead.establishment_name}
                    onChange={(e) => setNewLead({ ...newLead, establishment_name: e.target.value })}
                  />
                  <Input
                    placeholder="Ville"
                    value={newLead.city}
                    onChange={(e) => setNewLead({ ...newLead, city: e.target.value })}
                  />
                  <Textarea
                    placeholder="Notes"
                    value={newLead.notes}
                    onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={closeDialog}>
                    Annuler
                  </Button>
                  <Button onClick={handleAddLead} disabled={!newLead.full_name || !newLead.email}>
                    Ajouter
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="bg-gradient-to-br from-pink-500/10 to-pink-600/5 border-pink-500/20">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-pink-600">
                  <Target className="w-4 h-4" />
                  Total
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-pink-600">{stats.total}</div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-blue-600">
                  <TrendingUp className="w-4 h-4" />
                  Cette semaine
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{stats.newThisWeek}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  🌐 Site web
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.bySource.website}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  📘 Facebook
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.bySource.facebook}</div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-green-600">
                  ✅ Convertis
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.byStatus.converted}</div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-amber-600">
                  <Users className="w-4 h-4" />
                  Taux conversion
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-600">
                  {stats.total > 0 ? Math.round((stats.byStatus.converted / stats.total) * 100) : 0}%
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher par nom, email, établissement..."
                    className="pl-10"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>
              </div>

              <Select
                value={filters.source}
                onValueChange={(value) => setFilters({ ...filters, source: value as LeadSource | 'all' })}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes sources</SelectItem>
                  <SelectItem value="website">🌐 Site web</SelectItem>
                  <SelectItem value="facebook">📘 Facebook</SelectItem>
                  <SelectItem value="instagram">📷 Instagram</SelectItem>
                  <SelectItem value="manual">✏️ Manuel</SelectItem>
                  <SelectItem value="other">📋 Autre</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value as LeadStatus | 'all' })}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous statuts</SelectItem>
                  <SelectItem value="new">🆕 Nouveau</SelectItem>
                  <SelectItem value="contacted">📞 Contacté</SelectItem>
                  <SelectItem value="qualified">⭐ Qualifié</SelectItem>
                  <SelectItem value="demo_scheduled">📅 Démo planifiée</SelectItem>
                  <SelectItem value="converted">✅ Converti</SelectItem>
                  <SelectItem value="lost">❌ Perdu</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.period}
                onValueChange={(value) => setFilters({ ...filters, period: value as '7d' | '30d' | '90d' | 'all' })}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Période" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">7 derniers jours</SelectItem>
                  <SelectItem value="30d">30 derniers jours</SelectItem>
                  <SelectItem value="90d">90 derniers jours</SelectItem>
                  <SelectItem value="all">Tout</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Leads Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Prospects ({leads.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact</TableHead>
                  <TableHead>Établissement</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Emails</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{lead.full_name}</p>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-primary">
                            <Mail className="w-3 h-3" />
                            {lead.email}
                          </a>
                          {lead.phone && (
                            <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-primary">
                              <Phone className="w-3 h-3" />
                              {lead.phone}
                            </a>
                          )}
                        </div>
                        {/* Afficher les infos de qualification si disponibles */}
                        {(lead.current_booking_channels?.length || lead.current_management_system?.length) && (
                          <div className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
                            {lead.current_booking_channels && lead.current_booking_channels.length > 0 && (
                              <div className="flex items-center gap-1">
                                <span className="opacity-60">📥</span>
                                <span>Réservations: {lead.current_booking_channels.join(", ")}</span>
                              </div>
                            )}
                            {lead.current_management_system && lead.current_management_system.length > 0 && (
                              <div className="flex items-center gap-1">
                                <span className="opacity-60">📋</span>
                                <span>Gestion: {lead.current_management_system.join(", ")}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {lead.establishment_name ? (
                        <div>
                          <div className="flex items-center gap-1">
                            <Building2 className="w-3 h-3 text-muted-foreground" />
                            {lead.establishment_name}
                          </div>
                          {lead.city && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <MapPin className="w-3 h-3" />
                              {lead.city}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {getSourceIcon(lead.source)} {getSourceLabel(lead.source)}
                      </Badge>
                      {lead.source_campaign && (
                        <p className="text-xs text-muted-foreground mt-1">{lead.source_campaign}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      {lead.email_count > 0 ? (
                        <button
                          onClick={() => openDialog('emailHistory', lead.id)}
                          className="hover:bg-muted p-1.5 -m-1.5 rounded transition-colors text-left"
                        >
                          <div className="space-y-1">
                            <div className="flex items-center gap-1.5">
                              {lead.last_email_type === "first_contact" && (
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                  1er contact
                                </Badge>
                              )}
                              {lead.last_email_type === "followup" && (
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                  Relance
                                </Badge>
                              )}
                              {lead.last_email_type === "post_call" && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                  Post-appel
                                </Badge>
                              )}
                              {lead.last_email_type === "post_call_failed" && (
                                <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                                  Post-appel (échec)
                                </Badge>
                              )}
                               {lead.last_email_type === "post_call_success" && (
                                 <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                   Post-appel (succès)
                                 </Badge>
                               )}
                              {lead.email_count > 1 && (
                                <span className="text-xs text-muted-foreground">
                                  ({lead.email_count})
                                </span>
                              )}
                            </div>
                            {lead.last_email_sent_at && (
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(lead.last_email_sent_at), "dd/MM", { locale: fr })}
                              </p>
                            )}
                          </div>
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-auto p-0">
                            <Badge className={`${getStatusColor(lead.status)} cursor-pointer`}>
                              {getStatusLabel(lead.status)}
                              <ChevronDown className="w-3 h-3 ml-1" />
                            </Badge>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem onClick={() => handleStatusChange(lead, 'new')}>
                            🆕 Nouveau
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusChange(lead, 'contacted')}>
                            📞 Contacté
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusChange(lead, 'qualified')}>
                            ⭐ Qualifié
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusChange(lead, 'demo_scheduled')}>
                            📅 Démo planifiée
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusChange(lead, 'converted')}>
                            ✅ Converti
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusChange(lead, 'lost')}>
                            ❌ Perdu
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="w-3 h-3 text-muted-foreground" />
                        <span title={format(new Date(lead.created_at), "dd/MM/yyyy HH:mm", { locale: fr })}>
                          {formatDistanceToNow(new Date(lead.created_at), { addSuffix: true, locale: fr })}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                              <Send className="w-4 h-4 mr-2" />
                              Envoyer un email
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              <DropdownMenuItem onClick={() => handleOpenEmailDialog(lead, "custom")}>
                                ✏️ Email libre
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleOpenEmailDialog(lead, "first_contact")}>
                                ✉️ Premier contact
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleOpenEmailDialog(lead, "followup")}>
                                🔄 Relance
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleOpenEmailDialog(lead, "post_call")}>
                                📞 Post-appel
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleOpenEmailDialog(lead, "post_call_failed")}>
                                📞❌ Post-appel (échec)
                              </DropdownMenuItem>
                               <DropdownMenuItem onClick={() => handleOpenEmailDialog(lead, "post_call_success")}>
                                 📞✅ Post-appel (succès)
                               </DropdownMenuItem>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleOpenEditDialog(lead)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Modifier le prospect
                          </DropdownMenuItem>
                          {lead.email_count > 0 && (
                            <DropdownMenuItem onClick={() => openDialog('emailHistory', lead.id)}>
                              <Mail className="w-4 h-4 mr-2" />
                              Voir les emails envoyés ({lead.email_count})
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => openDialog('callLog', lead.id)}>
                            <FileText className="w-4 h-4 mr-2" />
                            Journal d'appels
                            {callLogCounts[lead.id] > 0 && (
                              <span className="ml-auto text-xs text-muted-foreground">
                                ({callLogCounts[lead.id]})
                              </span>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleOpenNoteDialog(lead)}>
                            <MessageSquare className="w-4 h-4 mr-2" />
                            {lead.notes ? "Modifier les notes" : "Ajouter une note"}
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <a href={`mailto:${lead.email}`}>
                              <Mail className="w-4 h-4 mr-2" />
                              Email direct
                            </a>
                          </DropdownMenuItem>
                          {lead.phone && (
                            <DropdownMenuItem asChild>
                              <a href={`tel:${lead.phone}`}>
                                <Phone className="w-4 h-4 mr-2" />
                                Appeler
                              </a>
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDeleteLead(lead)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {leads.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Aucun prospect trouvé
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Note Dialog */}
        <Dialog open={noteDialogOpen} onOpenChange={(open) => !open && closeDialog()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Notes pour {dialogLead?.full_name}</DialogTitle>
              <DialogDescription>
                Ajoutez des notes de suivi pour ce prospect
              </DialogDescription>
            </DialogHeader>
            <Textarea
              placeholder="Vos notes..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={5}
            />
            <DialogFooter>
              <Button variant="outline" onClick={closeDialog}>
                Annuler
              </Button>
              <Button onClick={handleSaveNotes}>
                Sauvegarder
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email Dialog */}
        <SendLeadEmailDialog
          open={emailDialogOpen}
          onOpenChange={(open) => !open && closeDialog()}
          lead={dialogLead ? {
            id: dialogLead.id,
            full_name: dialogLead.full_name,
            email: dialogLead.email,
            phone: dialogLead.phone || null,
            establishment_name: dialogLead.establishment_name || null,
            city: dialogLead.city || null,
            email_count: dialogLead.email_count || 0,
            last_email_type: dialogLead.last_email_type || null,
            last_email_sent_at: dialogLead.last_email_sent_at || null,
          } : null}
          emailType={dialogEmailType || "first_contact"}
          onSuccess={() => {
            refetch();
            closeDialog();
          }}
        />

        {/* Email History Dialog */}
        <LeadEmailHistoryDialog
          open={emailHistoryOpen}
          onOpenChange={(open) => !open && closeDialog()}
          leadId={dialogLead?.id || ""}
          leadName={dialogLead?.full_name || ""}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => !open && closeDialog()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce prospect ?</AlertDialogTitle>
              <AlertDialogDescription>
                Vous êtes sur le point de supprimer définitivement le prospect{" "}
                <strong>{dialogLead?.full_name}</strong> ({dialogLead?.email}).
                <br /><br />
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={confirmDeleteLead}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Lead Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => !open && closeDialog()}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifier le prospect</DialogTitle>
              <DialogDescription>
                Modifiez les informations de ce prospect
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              {/* Informations de contact */}
              <div className="space-y-4 md:col-span-2">
                <h3 className="font-medium text-sm text-muted-foreground">Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">Nom complet *</Label>
                    <Input
                      id="edit-name"
                      value={editingLead?.full_name || ""}
                      onChange={(e) => setEditingLead({...editingLead, full_name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-email">Email *</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editingLead?.email || ""}
                      onChange={(e) => setEditingLead({...editingLead, email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-phone">Téléphone</Label>
                    <Input
                      id="edit-phone"
                      value={editingLead?.phone || ""}
                      onChange={(e) => setEditingLead({...editingLead, phone: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-contact-pref">Préférence de contact</Label>
                    <Select
                      value={editingLead?.contact_preference || ""}
                      onValueChange={(value) => setEditingLead({...editingLead, contact_preference: value})}
                    >
                      <SelectTrigger id="edit-contact-pref">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Téléphone</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Établissement */}
              <div className="space-y-4 md:col-span-2">
                <h3 className="font-medium text-sm text-muted-foreground">Établissement</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-establishment">Nom de l'établissement</Label>
                    <Input
                      id="edit-establishment"
                      value={editingLead?.establishment_name || ""}
                      onChange={(e) => setEditingLead({...editingLead, establishment_name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-type">Type d'établissement</Label>
                    <Select
                      value={editingLead?.establishment_type || ""}
                      onValueChange={(value) => setEditingLead({...editingLead, establishment_type: value})}
                    >
                      <SelectTrigger id="edit-type">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="restaurant">Restaurant</SelectItem>
                        <SelectItem value="bar">Bar</SelectItem>
                        <SelectItem value="cafe">Café</SelectItem>
                        <SelectItem value="hotel">Hôtel</SelectItem>
                        <SelectItem value="other">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-city">Ville</Label>
                    <Input
                      id="edit-city"
                      value={editingLead?.city || ""}
                      onChange={(e) => setEditingLead({...editingLead, city: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-capacity">Capacité d'accueil</Label>
                    <Input
                      id="edit-capacity"
                      type="number"
                      value={editingLead?.seating_capacity || ""}
                      onChange={(e) => setEditingLead({...editingLead, seating_capacity: parseInt(e.target.value) || undefined})}
                    />
                  </div>
                </div>
              </div>

              {/* Qualification */}
              <div className="space-y-4 md:col-span-2">
                <h3 className="font-medium text-sm text-muted-foreground">Qualification</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-plan">Plan intéressé</Label>
                    <Select
                      value={editingLead?.interested_plan || ""}
                      onValueChange={(value) => setEditingLead({...editingLead, interested_plan: value})}
                    >
                      <SelectTrigger id="edit-plan">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gratuit">Gratuit</SelectItem>
                        <SelectItem value="starter">Starter</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-booking-channels">Canaux de réservation actuels</Label>
                    <Input
                      id="edit-booking-channels"
                      value={editingLead?.current_booking_channels?.join(", ") || ""}
                      onChange={(e) => setEditingLead({
                        ...editingLead, 
                        current_booking_channels: e.target.value ? e.target.value.split(",").map(s => s.trim()) : []
                      })}
                      placeholder="téléphone, email, TheFork..."
                    />
                    <p className="text-xs text-muted-foreground">Séparez par des virgules</p>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="edit-management-system">Système de gestion actuel</Label>
                    <Input
                      id="edit-management-system"
                      value={editingLead?.current_management_system?.join(", ") || ""}
                      onChange={(e) => setEditingLead({
                        ...editingLead, 
                        current_management_system: e.target.value ? e.target.value.split(",").map(s => s.trim()) : []
                      })}
                      placeholder="cahier / papier, Excel, autre logiciel..."
                    />
                    <p className="text-xs text-muted-foreground">Séparez par des virgules</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <Textarea
                  id="edit-notes"
                  value={editingLead?.notes || ""}
                  onChange={(e) => setEditingLead({...editingLead, notes: e.target.value})}
                  rows={4}
                  placeholder="Notes internes sur ce prospect..."
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={closeDialog}>
                Annuler
              </Button>
              <Button 
                onClick={handleSaveEdit} 
                disabled={!editingLead?.full_name || !editingLead?.email}
              >
                Sauvegarder
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Call Log Dialog */}
        <LeadCallLogDialog
          open={callLogDialogOpen}
          onOpenChange={(open) => !open && closeDialog()}
          lead={dialogLead}
          onCallLogAdded={() => fetchCallLogCounts(leads.map(l => l.id))}
        />
      </div>
    </div>
  );
}
